import { useState, useEffect, useMemo } from 'react';
import { getLearningProgramPlansService } from '../../../../../services/learning_programs.service';

export const usePlanGating = ({ modulesData = [], isPublicPreview = false }) => {
    // Plan-based session display limit
    const [currentPlan, setCurrentPlan] = useState(null);

    // Fetch the user's current plan to enforce session_display_limit
    useEffect(() => {
        if (isPublicPreview) return;
        getLearningProgramPlansService()
            .then(res => {
                if (res?.data?.current_plan) setCurrentPlan(res.data.current_plan);
            })
            .catch(() => {});
    }, [isPublicPreview]);

    // When embedded, only the first session is unlocked; all others are locked
    const firstSessionCode = useMemo(() => {
        for (const mod of modulesData) {
            for (const unit of mod.units || []) {
                if (unit.lessons && unit.lessons.length > 0) return unit.lessons[0].session_code;
            }
        }
        return null;
    }, [modulesData]);

    // Set of session codes locked by the current plan's session_display_limit.
    // If session_display_limit is null the set is empty (show all).
    // Sessions are sorted by parsing the M#_U#_S# ordinals from their session_code
    // so that the first N sessions in canonical order are always unlocked.
    const lockedSessionCodes = useMemo(() => {
        if (isPublicPreview) return new Set();
        let limit = currentPlan?.session_display_limit;
        // If plan_key is null/missing, treat the user as on the default (free) plan → only first session unlocked
        if (!currentPlan?.plan_key) limit = 1;
        if (limit === null || limit === undefined) return new Set();

        // Build flat list of all session codes across all modules/units
        const allSessionCodes = [];
        for (const mod of modulesData) {
            for (const unit of mod.units || []) {
                for (const lesson of unit.lessons || []) {
                    allSessionCodes.push(lesson.session_code);
                }
            }
        }

        // Parse M#_U#_S# into sortable numeric tuple; unrecognised codes sort last
        const parseCode = (code) => {
            const m = (code || '').match(/M(\d+)[_-]U(\d+)[_-]S(\d+)/i);
            return m ? [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)] : [Infinity, Infinity, Infinity];
        };

        allSessionCodes.sort((a, b) => {
            const [am, au, as_] = parseCode(a);
            const [bm, bu, bs] = parseCode(b);
            if (am !== bm) return am - bm;
            if (au !== bu) return au - bu;
            return as_ - bs;
        });

        // First `limit` sessions are unlocked; everything beyond is locked
        return new Set(allSessionCodes.slice(limit));
    }, [modulesData, currentPlan, isPublicPreview]);

    return {
        currentPlan,
        setCurrentPlan,
        lockedSessionCodes,
        firstSessionCode,
    };
};

export default usePlanGating;
